import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Descriptions, Divider, Flex, Input, List, Modal, Select, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { defaultDevice, DeviceContext, DeviceResponseType, UpdateDeviceContext } from "../../../context/deviceContext";
import isEqual from "lodash/isEqual";
import { CompanyContext, UpdateCompanyContext } from "../../../context/companyContext";
import { PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../util/helper";

export function DeviceManage() {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const { device } = useContext(DeviceContext)
  const { refreshDevice, updateDevice, registerDevice, regenerateDeviceKey } = useContext(UpdateDeviceContext)
  const { refreshCompanies } = useContext(UpdateCompanyContext)
  const { companies } = useContext(CompanyContext)

  const [deviceInfo, setDeviceInfo] = useState<DeviceResponseType>(defaultDevice)
  const [originalDeviceInfo, setOriginalDeviceInfo] = useState<DeviceResponseType>(defaultDevice)
  const [errors, setErros] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isPageReady, setIsPageReady] = useState(deviceId === 'register')

  useEffect(() => {
    let isCancelled = false

    const validateAndLoadDeviceManage = async () => {
      if (!deviceId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      if (deviceId === 'register') {
        setIsPageReady(true)
        refreshCompanies({ page: 1, limit: 1000 })
        return
      }

      setIsPageReady(false)

      try {
        const deviceResult = await refreshDevice(deviceId)

        if (isCancelled) {
          return
        }

        if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
          return
        }

        setIsPageReady(true)
      } catch (error) {
        console.error("Error validating device manage.", error)
        if (isCancelled) {
          return
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
      }
    }

    validateAndLoadDeviceManage()

    return () => {
      isCancelled = true
    }
  }, [deviceId, navigate, refreshCompanies, refreshDevice])

  useEffect(() => {
    if (!device || deviceId === 'register' || !deviceId) {
      setDeviceInfo(defaultDevice)
      setOriginalDeviceInfo(defaultDevice)
      return
    }

    setDeviceInfo(device)
    setOriginalDeviceInfo(device)
  }, [device, deviceId])

  const dataSource = useMemo(() => {
    const commonData = [
      {
        key: 'company_name',
        label: '企業',
        value: deviceInfo?.company?.company_name || '',
        disabled: deviceId !== 'register',
      },
      {
        key: 'location',
        label: '拠点',
        value: deviceInfo?.location || '',
        disabled: false,
        onchange: (el: React.ChangeEvent<HTMLInputElement>) => { setDeviceInfo((prev) => ({ ...prev, location: el.target.value })) }
      },
      {
        key: 'name',
        label: 'デバイス名',
        value: deviceInfo?.name || '',
        disabled: false,
        onchange: (el: React.ChangeEvent<HTMLInputElement>) => { setDeviceInfo((prev) => ({ ...prev, name: el.target.value })) }
      },
    ]

    if (deviceId !== 'register') {
      commonData.push({
        key: 'api',
        label: 'API Key',
        value: deviceInfo?.api_key || '',
        disabled: true
      })
    }

    return commonData
  }, [deviceId, deviceInfo])

  const onClickRegister = useCallback(async () => {
    const newErrors: Record<string, boolean> = {};
    const requiredFields = deviceId === 'register' ? ['company_id', 'location', 'name'] : ['location', 'name']

    requiredFields.forEach((key) => {
      if (!deviceInfo[key as keyof typeof deviceInfo]) {
        newErrors[key] = true
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErros(newErrors)
      const message = newErrors.company_id ? '企業を選択してください。' : '拠点名とデバイス名は必須です。';
      Modal.warning({ content: message, centered: true })
      return
    }

    setErros({})
    setIsLoading(true)

    try {
      if (deviceId === 'register') {
        const payload = {
          company_id: deviceInfo.company_id,
          location: deviceInfo.location,
          name: deviceInfo.name,
        }
        const res = await registerDevice(payload)
        if (res) {
          Modal.success({ content: 'デバイスを新規登録しました！', centered: true, onOk() { navigate(-1) } })
        } else {
          Modal.error({ content: 'デバイスの登録に失敗しました。', centered: true })
        }
      } else {
        const res = await updateDevice({ location: deviceInfo.location, name: deviceInfo.name, device_id: deviceId! })
        if (res) {
          Modal.success({ content: 'デバイスの更新が完了しました!', centered: true, onOk() { navigate(-1) } })
        } else {
          Modal.error({ content: 'デバイスの更新に失敗しました。', centered: true })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [deviceId, deviceInfo, navigate, registerDevice, updateDevice])

  const onClickBack = useCallback(() => { navigate(-1) }, [navigate])

  const handleRegenerate = useCallback(async () => {
    if (!deviceId || deviceId === 'register') return

    const hasUnsavedChanges = !isEqual(originalDeviceInfo, deviceInfo)

    if (!hasUnsavedChanges) {
      Modal.warning({
        title: 'API Keyを再発行しますか？',
        content: '現在のAPI Keyは直ちに使用できなくなります。',
        okCancel: true,
        centered: true,
        okType: 'danger',
        async onOk() {
          setIsLoading(true)
          try {
            const regenerateResult = await regenerateDeviceKey(deviceId)
            if (!regenerateResult) {
              Modal.error({ content: 'API Keyの再発行に失敗しました。', centered: true })
              return
            }

            const deviceResult = await refreshDevice(deviceId)
            if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
              return
            }

            Modal.success({ content: 'API Keyを再発行しました!', centered: true, onOk() { navigate(-1) } })
          } catch {
            Modal.error({ content: 'API Keyの再発行に失敗しました。', centered: true })
          } finally {
            setIsLoading(false)
          }
        }
      })
      return
    }

    Modal.warning({
      title: '未保存の変更があります。',
      content: '変更を保存してAPI Keyを再発行しますか？',
      okCancel: true,
      centered: true,
      okType: 'danger',
      async onOk() {
        setIsLoading(true)
        try {
          const updateResult = await updateDevice({ device_id: deviceId, location: deviceInfo.location, name: deviceInfo.name })
          if (!updateResult) {
            Modal.error({ content: 'デバイス情報の更新に失敗しました。', centered: true })
            return
          }

          const regenerateResult = await regenerateDeviceKey(deviceId)
          if (!regenerateResult) {
            Modal.error({ content: 'API Keyの再発行に失敗しました。', centered: true })
            return
          }

          const deviceResult = await refreshDevice(deviceId)
          if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
            return
          }

          Modal.success({ content: 'デバイス情報を更新し、API Keyを再発行しました。', centered: true, onOk() { navigate(-1) } })
        } catch {
          Modal.error({ content: '処理中にエラーが発生しました。', centered: true })
        } finally {
          setIsLoading(false)
        }
      }
    })
  }, [deviceId, deviceInfo, originalDeviceInfo, refreshDevice, regenerateDeviceKey, updateDevice, navigate, setIsLoading])

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip="読み込み中..." />
      </Flex>
    )
  }

  return (
    <div>
      <Spin spinning={isLoading} fullscreen tip='処理中...' />
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">デバイス{deviceId === 'register' ? '登録' : '編集'}画面</p>
      </div>
      <Divider />
      <Flex vertical align="center">
        <List
          header={<div className="h-4"></div>}
          bordered
          dataSource={dataSource}
          renderItem={(item) => (
            <List.Item style={{ background: '#FFFFFF', justifyContent: 'end' }} >
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={item.label} key={item.key} >
                  {item.key === 'company_name' && deviceId === 'register' ? (
                    <Select
                      value={deviceInfo.company_id}
                      onChange={(value) => setDeviceInfo(prev => ({ ...prev, company_id: value }))}
                      status={errors.company_id ? 'error' : undefined}
                      className="w-[320px] text-start"
                      placeholder="企業を選択してください"
                    >
                      {companies.map(c => (
                        <Select.Option key={c.id} value={c.id}>{c.company_name}</Select.Option>
                      ))}
                    </Select>
                  ) : item.key === 'api' ? (
                    <div className="flex space-x-2 w-[320px]">
                      <Input value={item.value} disabled />
                      <Button variant="outlined" color="primary" onClick={handleRegenerate}>再発行</Button>
                    </div>
                  ) : (
                    <Input
                      value={item.value}
                      disabled={item.disabled}
                      onChange={item.onchange}
                      status={errors[item.key] ? 'error' : undefined}
                      className="w-[320px]"
                    />
                  )}
                </Descriptions.Item>
              </Descriptions>
            </List.Item>
          )}
          className="overflow-hidden bg-[#FAFAFA] text-center"
        />
        <Flex gap="large" className="mt-5">
          <Button variant="outlined" color="primary" size="large" onClick={onClickBack}>戻る</Button>
          <Button type="primary" size="large" onClick={onClickRegister}>{deviceId === 'register' ? '登録' : '更新'}</Button>
        </Flex>
      </Flex>
    </div>
  );
}
